import React, { useState } from 'react';
import { useAtlas } from '../../context/AtlasContext';
import { AtlasMapService } from '../../lib/atlasMapService';
import { XIcon, SparkleIcon, MapPinIcon } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase';

interface LocationCreateModalProps {
    placement: { x: number; y: number } | null;
    onClose: () => void;
}

export const LocationCreateModal: React.FC<LocationCreateModalProps> = ({ placement, onClose }) => {
    const { createLocation, placeLocation, currentIdeaId } = useAtlas();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState<'exterior' | 'interior'>('exterior');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canGenerate = name.trim() && description.trim();
    const canSave = name.trim() && (previewUrl || !placement); // Can save without image if not placing

    const handleGenerate = async () => {
        if (!canGenerate) return;

        setIsGenerating(true);
        setError(null);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                setError('Not authenticated');
                return;
            }

            // Generate preview image
            const imageUrl = await AtlasMapService.generateLocationImage(
                description,
                user.id,
                `preview_${Date.now()}`
            );

            if (imageUrl) {
                setPreviewUrl(imageUrl);
            } else {
                setError('Failed to generate image. Please try again.');
            }
        } catch (err) {
            console.error('Generation error:', err);
            setError('An error occurred during generation.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSave = async () => {
        if (!canSave) return;

        setIsSaving(true);
        setError(null);

        try {
            // Create the location
            const location = await createLocation(name, description, type);

            if (!location) {
                setError('Failed to create location.');
                return;
            }

            // If we have a placement position and image, place on canvas
            if (placement && previewUrl) {
                await placeLocation(location.id, placement.x, placement.y, previewUrl);
            }

            onClose();
        } catch (err) {
            console.error('Save error:', err);
            setError('An error occurred while saving.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal - centered in canvas area (accounting for left toolbar ~80px and right sidebar 288px) */}
            <div
                className="relative w-full max-w-lg bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
                style={{
                    // Offset to account for sidebars: (leftToolbar - rightSidebar) / 2 = (80 - 288) / 2 = -104px
                    marginRight: '208px' // Shift left to center in canvas (288 - 80 = 208px offset)
                }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                    <div className="flex items-center gap-2">
                        <MapPinIcon className="w-5 h-5 text-indigo-400" weight="duotone" />
                        <h2 className="text-lg font-bold text-white">Create Location</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* Name */}
                    <div>
                        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1.5 block">
                            Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Haunted Forest"
                            className="w-full px-4 py-3 bg-zinc-900/50 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/50 transition-colors"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1.5 block">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe the location for map generation..."
                            rows={3}
                            className="w-full px-4 py-3 bg-zinc-900/50 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/50 transition-colors resize-none"
                        />
                    </div>

                    {/* Type */}
                    <div>
                        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1.5 block">
                            Type
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setType('exterior')}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                    type === 'exterior'
                                        ? 'bg-indigo-500 text-white'
                                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                }`}
                            >
                                Exterior
                            </button>
                            <button
                                onClick={() => setType('interior')}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                    type === 'interior'
                                        ? 'bg-indigo-500 text-white'
                                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                }`}
                            >
                                Interior
                            </button>
                        </div>
                    </div>

                    {/* Preview Area */}
                    {placement && (
                        <div>
                            <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1.5 block">
                                Map Preview
                            </label>
                            <div className="relative aspect-square bg-zinc-900 border border-white/10 rounded-xl overflow-hidden">
                                {previewUrl ? (
                                    <img
                                        src={previewUrl}
                                        alt="Location preview"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-600">
                                        <MapPinIcon className="w-12 h-12 mb-2" />
                                        <p className="text-sm">
                                            {isGenerating ? 'Generating...' : 'Click Generate to preview'}
                                        </p>
                                    </div>
                                )}

                                {isGenerating && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                            <span className="text-sm text-zinc-300">Generating map...</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Generate Button */}
                            <button
                                onClick={handleGenerate}
                                disabled={!canGenerate || isGenerating}
                                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-900 disabled:text-zinc-600 text-white font-medium rounded-lg transition-colors"
                            >
                                <SparkleIcon className="w-4 h-4" />
                                <span>{previewUrl ? 'Regenerate' : 'Generate'} Preview</span>
                            </button>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-sm text-red-400">{error}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/5">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!canSave || isSaving}
                        className="px-6 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-white font-medium rounded-lg transition-colors"
                    >
                        {isSaving ? 'Saving...' : placement ? 'Save & Place' : 'Save Location'}
                    </button>
                </div>
            </div>
        </div>
    );
};
